import * as echarts from "echarts";
import type { ChartConfig } from "@bi-studio/shared/lib";
import type { ChartData, ChartSize } from "./types";
import {
  getChartInstance,
  setupResize,
  clearContainer,
  resolveColors,
} from "./utils";

export const renderPieChart = (
  container: HTMLElement | null,
  data: ChartData,
  config: ChartConfig,
  size?: ChartSize,
  theme: 'light' | 'dark' = 'light'
): HTMLElement | null => {
  if (!container || typeof window === "undefined") return null;

  const width = size?.width ?? 360;
  const height = size?.height ?? 360;

  clearContainer(container);

  // Set container size
  container.style.width = "100%";
  container.style.height = "100%";

  const chartInstance = getChartInstance(container, theme);
  const colors = resolveColors(config, theme);

  // Get label field (could be labelField or categoryField for backward compatibility)
  const labelField =
    config.labelField || (config as { categoryField?: string }).categoryField;
  const valueField = config.valueField;

  // Transform data to ECharts format
  const pieData = data.map((row, index) => ({
    name: labelField ? String(row[labelField ?? ""] ?? "") : `Item ${index + 1}`,
    value: Number(row[valueField ?? ""]) || 0,
  }));

  // Calculate radius based on innerRadius (matching original D3.js behavior)
  const margin = size?.margin ?? { top: 32, right: 32, bottom: 32, left: 32 };
  const availableSize = Math.min(
    width - (margin.left ?? 0) - (margin.right ?? 0),
    height - (margin.top ?? 0) - (margin.bottom ?? 0)
  );
  const maxRadius = availableSize / 2;

  const innerRadius = config.innerRadius ?? 0;
  // If innerRadius > 1, treat as pixels; otherwise treat as ratio (0-1)
  const innerRadiusValue =
    innerRadius > 1 ? innerRadius : innerRadius * maxRadius;

  // Convert to percentage for ECharts (outer radius is 70% of container)
  const outerRadiusPercent = 70;
  const innerRadiusPercent =
    innerRadiusValue > 0
      ? (innerRadiusValue / maxRadius) * outerRadiusPercent
      : 0;

  const radius: string | [string, string] =
    innerRadiusPercent > 0
      ? [`${Math.max(innerRadiusPercent, 5)}%`, `${outerRadiusPercent}%`] // Min 5% to avoid too small inner radius
      : `${outerRadiusPercent}%`;

  const option: echarts.EChartsOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: "item",
      formatter: "{a} <br/>{b}: {c} ({d}%)",
    },
    legend: {
      orient: "vertical",
      left: "left",
      top: config.title ? "20%" : "10%",
      show: true,
    },
    series: [
      {
        name: config.title || "Data",
        type: "pie",
        radius,
        center: ["50%", "55%"],
        data: pieData,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.5)",
          },
        },
        label: {
          show: true,
          formatter: (params: any) => {
            const name = params.name || "";
            const value = params.value || 0;
            const valueText = value % 1 === 0 ? value.toString() : value.toFixed(1);
            return `${name}\n${valueText}`;
          },
          fontSize: 13,
          fontWeight: "bold",
        },
        labelLine: {
          show: true,
        },
        itemStyle: {
          color: (params: any) => {
            return colors[params.dataIndex % colors.length];
          },
        },
      },
    ],
  };

  chartInstance.setOption(option);
  setupResize(container, chartInstance);

  return container;
};
